/** @fileoverview Loads the files already sent in a conversation and switches them on or off. */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteAttachment,
  listConversationFiles,
  setAttachmentEnabled,
} from '../services/attachmentsApi';
import type { ConversationFilesResponse } from '../types/attachments';

/** `conversationId` is undefined for a new chat, which has nothing sent yet. */
export function useConversationFiles(
  tenantId: string,
  conversationId: string | undefined,
  refreshKey: string,
  onError: (message: string) => void,
  onDeleted?: () => void
) {
  const [data, setData] = useState<ConversationFilesResponse>();
  const [reload, setReload] = useState(0);
  const latestToggle = useRef(new Map<string, number>());

  useEffect(() => {
    if (!conversationId) {
      setData(undefined);
      return undefined;
    }
    const controller = new AbortController();
    setData(undefined);
    listConversationFiles(tenantId, conversationId, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setData(response);
      })
      .catch(() => {
        if (!controller.signal.aborted) setData(undefined);
      });
    return () => controller.abort();
  }, [tenantId, conversationId, refreshKey, reload]);

  const toggle = useCallback(
    async (attachmentId: string, enabled: boolean) => {
      if (!conversationId) return;
      const setSwitch = (value: boolean) =>
        setData(
          (current) =>
            current && {
              ...current,
              attachments: current.attachments.map((file) =>
                file.id === attachmentId ? { ...file, enabled: value } : file
              ),
            }
        );
      const requestId = (latestToggle.current.get(attachmentId) ?? 0) + 1;
      latestToggle.current.set(attachmentId, requestId);
      const isLatest = () => latestToggle.current.get(attachmentId) === requestId;
      setSwitch(enabled);
      try {
        const reply = await setAttachmentEnabled(tenantId, conversationId, attachmentId, enabled);
        // Only the latest reply counts; an older one would show stale placements.
        if (!isLatest()) return;
        if (reply?.attachments && reply.budget) {
          setData({ attachments: reply.attachments, budget: reply.budget });
        } else {
          setReload((current) => current + 1);
        }
      } catch {
        if (!isLatest()) return;
        setSwitch(!enabled);
        onError('The file could not be switched. Try again.');
        setReload((current) => current + 1);
      }
    },
    [conversationId, onError, tenantId]
  );

  /** Removes a sent file everywhere; resolves true on success. */
  const remove = useCallback(
    async (attachmentId: string) => {
      if (!conversationId) return false;
      try {
        await deleteAttachment(tenantId, conversationId, attachmentId);
        setReload((current) => current + 1);
        onDeleted?.();
        return true;
      } catch {
        onError('The file could not be deleted. Try again.');
        return false;
      }
    },
    [conversationId, onDeleted, onError, tenantId]
  );

  return { files: data, toggle, remove };
}
